import { forwardRef, useCallback, useState } from "react";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { styled, Text as TamaguiText, useMedia, useProps, View } from "@tamagui/core";
import { ChevronLeft, ChevronRight } from "../../__generated__/icons";
import { getStateTokenValue } from "../../helpers/tokens";
import { createAnchorOrButtonFrame } from "../shared/anchorOrButton";
import { Text } from "../Text";
import { jsx, jsxs } from "react/jsx-runtime";
const { textButton: textButtonTokens, button: buttonTokens } = tokens, sharedFrameConfig = {
  name: "TextButton",
  display: "flex",
  flexDirection: "row",
  gap: textButtonTokens.padding.labelIconGap.default,
  userSelect: "none",
  cursor: "pointer",
  // Browser resets
  padding: 0,
  borderWidth: 0,
  backgroundColor: "$transparent",
  focusVisibleStyle: {
    outlineStyle: "solid",
    outlineWidth: textButtonTokens.borderWidth.state.default,
    outlineOffset: textButtonTokens.padding.state.focused.default,
    borderRadius: textButtonTokens.cornerRadius.state.focused
  },
  pressStyle: {
    scale: getStateTokenValue(buttonTokens.state.pressedScale)
  },
  variants: {
    variant: (variant) => ({
      focusVisibleStyle: {
        outlineColor: variant === "secondaryInverse" ? textButtonTokens.color.border.state.inverse : textButtonTokens.color.border.state.default
      }
    }),
    disabled: {
      true: {
        pointerEvents: "none"
      }
    }
  }
}, AnchorFrame = styled(TamaguiText, {
  ...sharedFrameConfig,
  tag: "a",
  accessibilityRole: "link",
  textDecorationLine: "none",
  display: "flex",
  position: "relative"
}), ButtonFrame = styled(View, {
  ...sharedFrameConfig,
  tag: "button",
  accessibilityRole: "button"
}), AnchorOrButtonFrame = createAnchorOrButtonFrame({
  AnchorFrame,
  ButtonFrame
}), TextButton = forwardRef(function({
  variant,
  disabled,
  label,
  onHoverIn,
  onHoverOut,
  chevronIcon,
  fontWeight,
  ...rest
}, ref) {
  const { size = "medium" } = useProps(rest), [isHovering, setIsHovering] = useState(!1), { hoverNone } = useMedia(), handleHoverIn = useCallback(
    (e) => {
      !hoverNone && setIsHovering(!0), onHoverIn?.(e);
    },
    [hoverNone, onHoverIn]
  ), handleHoverOut = useCallback(
    (e) => {
      !hoverNone && setIsHovering(!1), onHoverOut?.(e);
    },
    [hoverNone, onHoverOut]
  ), color = disabled ? textButtonTokens.color.content.state.inactive[variant] : isHovering ? textButtonTokens.color.content.state.hover[variant] : textButtonTokens.color.content[variant];
  return /* @__PURE__ */ jsxs(
    AnchorOrButtonFrame,
    {
      variant,
      disabled,
      "aria-disabled": disabled,
      focusable: !disabled,
      onHoverIn: handleHoverIn,
      onHoverOut: handleHoverOut,
      ref,
      ...rest,
      children: [
        chevronIcon === "left" && /* @__PURE__ */ jsx(ChevronLeft, { size: size === "large" ? 24 : 20, color }),
        /* @__PURE__ */ jsx(
          Text,
          {
            tag: "span",
            typography: textButtonTokens.typography[size],
            textDecorationLine: variant === "primary" || chevronIcon ? "none" : "underline",
            color,
            fontWeight: fontWeight ? textButtonTokens.fontWeight[fontWeight] : void 0,
            children: label
          }
        ),
        chevronIcon === "right" && /* @__PURE__ */ jsx(ChevronRight, { size: size === "large" ? 24 : 20, color })
      ]
    }
  );
});
export {
  TextButton
};
